import { createAsyncThunk } from '@reduxjs/toolkit';
import type { ApprovalRequest, StreamEvent } from '../models';
import { sendMessageWebSocket } from '../repositories/agentRepository';
import { toError } from '../services';
import {
  approvalSent,
  errorCleared,
  errorOccurred,
  finalResponseQueued,
  messageSent,
  streamEnded,
  threadIdUpdated,
  tokenReceived,
} from './agentChatSlice';
import { handleStreamEvent } from './eventHandlers';
import type { RootState } from './index';
import { TokenBuffer } from './tokenBuffer';

interface StreamRequestParams {
  message: string;
  companyCode: string;
  jobContext: Record<string, unknown> | null;
  threadId: string;
  approvalDecision?: { approvalId: string; decision: string; editMessage?: string } | null;
}

type Dispatch = (action: unknown) => unknown;

async function executeStreamedRequest(
  dispatch: Dispatch,
  wsParams: StreamRequestParams,
  signal: AbortSignal,
): Promise<void> {
  const tokenBuffer = new TokenBuffer((content) => {
    dispatch(tokenReceived(content));
  });

  let receivedApproval: ApprovalRequest | null = null;

  try {
    await sendMessageWebSocket(
      wsParams,
      (event: StreamEvent) => {
        handleStreamEvent(dispatch, event, tokenBuffer);
        if (event.event === 'approval_required') {
          receivedApproval = event.data;
        }
        if (event.event === 'run_end') {
          dispatch(threadIdUpdated(event.data.threadId));
          dispatch(
            finalResponseQueued({
              content: event.data.response ?? '',
              downloadLinks: event.data.downloadLinks ?? [],
            }),
          );
        }
      },
      signal,
    );

    // The stream may have resolved because it was aborted (thread switch /
    // new chat). Drop its buffered output instead of draining it into the
    // now-displayed thread.
    if (signal.aborted) {
      tokenBuffer.reset();
      return;
    }

    tokenBuffer.drain();
    dispatch(streamEnded({ jobContext: null, approvalRequest: receivedApproval }));
  } catch (e) {
    tokenBuffer.reset();
    // Aborting is intentional (thread switch / new chat), not a real failure.
    if (signal.aborted) return;
    dispatch(errorOccurred(toError(e)));
  }
}

export const sendMessage = createAsyncThunk(
  'agentChat/sendMessage',
  async (params: { content: string; companyCode: string }, { dispatch, getState, signal }) => {
    const state = (getState() as RootState).agentChat;
    dispatch(messageSent(params));

    await executeStreamedRequest(
      dispatch,
      {
        message: params.content,
        companyCode: params.companyCode,
        jobContext: state.lastJobContext,
        threadId: state.threadId,
      },
      signal,
    );
  },
);

export const retryMessage = createAsyncThunk('agentChat/retry', async (_, { dispatch, getState }) => {
  const state = (getState() as RootState).agentChat;
  if (!state.lastSent) return;
  dispatch(errorCleared());
  dispatch(sendMessage(state.lastSent));
});

export const sendApproval = createAsyncThunk(
  'agentChat/sendApproval',
  async (
    params: { label: string; decision: string; editMessage?: string },
    { dispatch, getState, signal },
  ) => {
    const state = (getState() as RootState).agentChat;
    if (!state.approvalRequest || !state.lastSent) return;

    dispatch(approvalSent(params.label));

    await executeStreamedRequest(
      dispatch,
      {
        message: '',
        companyCode: state.lastSent.companyCode,
        jobContext: null,
        threadId: state.threadId,
        approvalDecision: {
          approvalId: state.approvalRequest.approvalId,
          decision: params.decision,
          editMessage: params.editMessage,
        },
      },
      signal,
    );
  },
);
